import React, { Fragment, useMemo } from 'react';
import classNames from 'classnames';
import { Box, Divider, Skeleton, Stack } from '@mui/material';

import ErrorBoundary from 'components/ErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import useCustomError from 'hooks/useCustomError';
import { MoveTideLabel, type TideData } from 'types/tide';
import type { SunlightTimes } from 'types/sunlightTimes';

import TideChartContainer from './components/TideChartContainer';

import styles from './ForecastGraphTide.module.scss';

export interface ForecastGraphTideProps {
  dateFormat?: 'MDY' | 'DMY';
  daysToDisplay: Array<number>;
  startTimestamp: number;
  endTimestamp: number;
  sunlightTimes: SunlightTimes;
  tide: TideData;
}

export const TideContent: React.FC<ForecastGraphTideProps> = ({
  dateFormat = 'MDY',
  daysToDisplay,
  endTimestamp,
  startTimestamp,
  sunlightTimes,
  tide,
}) => {
  const flatData = useMemo(
    () =>
      daysToDisplay
        ?.map((day) => tide.days && tide?.days[day])
        .filter((day) => day)
        .flat(),
    [daysToDisplay, tide.days],
  );

  if (tide?.loading || !startTimestamp || !endTimestamp) {
    return (
      <Skeleton
        animation="wave"
        className={styles.loading}
        data-testid="tide-graph-loading"
        variant="rectangular"
      />
    );
  }

  const getTideLabel = (index: number, arrayLength: number) => {
    let label = MoveTideLabel.NONE;
    if (index === 0) label = MoveTideLabel.RIGHT;
    if (index === arrayLength - 1) label = MoveTideLabel.LEFT;
    return label;
  };

  return (
    <div className={styles.forecastGraphTideContainer} data-testid="tide-graph">
      <div className={styles.forecastGraphTideDays}>
        {daysToDisplay.map((dayIndex: number, displayIndex: number) => (
          <Fragment key={dayIndex}>
            <Divider
              className={classNames(dayIndex !== 0 && displayIndex !== 0 && styles.divider)}
              orientation="vertical"
              flexItem
              sx={{ border: 'none' }}
            />
            <Stack direction="column" key={dayIndex} width="100%" display="flex">
              <TideChartContainer
                tide={tide}
                sunlightTimes={sunlightTimes}
                flatData={flatData}
                startTimestamp={tide.days[dayIndex]?.[0].timestamp}
                endTimestamp={tide.days[dayIndex]?.[tide.days[dayIndex].length - 1].timestamp}
                moveTideLabel={getTideLabel(displayIndex, daysToDisplay.length)}
                dateFormat={dateFormat}
                isTableMode={false}
                dayIndex={dayIndex}
              />
            </Stack>
          </Fragment>
        ))}
      </div>
    </div>
  );
};

const ForecastGraphTide: React.FC<ForecastGraphTideProps> = (props) => {
  const {
    tide: { error },
  } = props;
  const { errorMessage, ErrorIcon, hasError } = useCustomError({ error });

  return (
    <ErrorBoundary
      error={hasError}
      errorBoundaryId="forecast-graph-tide"
      render={() => (
        <Box className={styles.error} data-testid="tide-graph-error">
          <ErrorMessage Icon={ErrorIcon} message={errorMessage} />
        </Box>
      )}
    >
      <TideContent {...props} />
    </ErrorBoundary>
  );
};

export default ForecastGraphTide;
